/* eslint-disable */
/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import {jwtDecode} from 'jwt-decode';
import {getAppOrigin} from 'coreUtils/url';
import {HTTPError} from 'coreUtils/adapters/sdk';
import {fetch} from 'coreUtils/utils';

const tokenCache = new Map();

/**
 * Parses the token and caches the decoded token
 * @param {string} token - The JWT bearer token to be parsed
 * @returns {object} - The decoded token
 */
export const parseToken = (token) => {
	if (!token) {
		return false;
	}

	if (!tokenCache.has(token)) {
		try {
			const decodedToken = jwtDecode(token.replace('Bearer ', ''));

			tokenCache.set(token, decodedToken);
		} catch (error) {
			console.error('Error decoding token', error);
			return false;
		}
	}

	return tokenCache.get(token);
};

/**
 * Decode SLAS JWT and extract information such as customer id, usid, etc.
 *
 */
export const parseSlasJWT = (jwt) => {
	const payload = parseToken(jwt)
	const {sub, isb, dnt} = payload

	if (!sub || !isb) {
		throw new Error('Unable to parse access token payload: missing sub and isb.')
	}

	const isbParts = isb.split('::')
	const uido = isbParts[0].split('uido:')[1]
	const isGuest = isbParts[1] === 'upn:Guest'
	const customerId = isGuest
		? isbParts[3].replace('gcid:', '')
		: isbParts[4].replace('rcid:', '')

	const loginId = isGuest ? 'guest' : isbParts[1].replace('upn:', '')

	const isAgent = !!isbParts?.[isGuest ? 5 : 6]?.startsWith('agent:')
	const agentId = isAgent ? isbParts?.[isGuest ? 5 : 6]?.replace('agent:', '') : null

	// SUB format
	// cc-slas::zzrf_001::scid:c9c45bfd-0ed3-4aa2-xxxx-40f88962b836::usid:b4865233-de92-4039-xxxx-aa2dfc8c1ea5
	const usid = sub.split('::')[3].replace('usid:', '')
	return {
		isGuest,
		customerId,
		usid,
		dnt,
		loginId,
		isAgent,
		agentId,
		uido
	}
}

/**
 * Compares the token age against the issued and expiry times. If the token's age is
 * within 60 seconds of its valid time (or exceeds it), we consider the token invalid.
 * @function
 * @param {string} token - The JWT bearer token to be inspected
 * @returns {boolean}
 */
export function isTokenValid(token) {
	const cachedDecodedToken = parseToken(token);

	if (cachedDecodedToken) {
		const {exp, iat} = cachedDecodedToken;
		const validTimeSeconds = exp - iat - 60;
		const tokenAgeSeconds = Date.now() / 1000 - iat;

		if (validTimeSeconds > tokenAgeSeconds) {
			return true;
		}
	}

	return false;
}

// Returns fomrulated body for SopperLogin getToken endpoint
export function createGetTokenBody(urlString, slasCallbackEndpoint, codeVerifier) {
	const url = new URL(urlString);
	const urlParams = new URLSearchParams(url.search);
	const usid = urlParams.get('usid');
	const code = urlParams.get('code');

	return {
		grantType: 'authorization_code_pkce',
		code,
		usid,
		codeVerifier: codeVerifier,
		redirectUri: slasCallbackEndpoint
	};
}

// Ocapi related utilities

const toCamel = (str) => {
	if (str.startsWith('_') || str.startsWith('c_')) {
		return str;
	}

	return str.replace(/([-_][a-z])/gi, ($1) => {
		return $1
			.toUpperCase()
			.replace('-', '')
			.replace('_', '');
	});
};

const isObject = (obj) => {
	return obj === Object(obj) && !Array.isArray(obj) && typeof obj !== 'function';
};

export const keysToCamel = (obj) => {
	if (isObject(obj)) {
		const n = {};

		Object.keys(obj).forEach((k) => {
			n[toCamel(k)] = keysToCamel(obj[k]);
		});

		return n;
	} else if (Array.isArray(obj)) {
		return obj.map((i) => {
			return keysToCamel(i);
		});
	}

	return obj;
};

export const camelCaseKeysToUnderscore = (_obj) => {
	if (typeof _obj != 'object') return _obj;

	// Copy the incoming object so we dont mutate it
	let obj;

	if (Array.isArray(_obj)) {
		obj = [..._obj];
	} else {
		obj = {..._obj};
	}

	for (var oldName in obj) {
		// Camel to underscore
		if (oldName.startsWith('c_')) {
			// Skip conversion for custom attributes and keep camelCase
			continue;
		}

		let newName = oldName.replace(/([A-Z])/g, ($1) => {
			return '_' + $1.toLowerCase();
		});

		// Only process if names are different
		if (newName != oldName) {
			// Check for the old property name to avoid a ReferenceError in strict mode.
			if (Object.prototype.hasOwnProperty.call(obj, oldName)) {
				obj[newName] = obj[oldName];
				delete obj[oldName];
			}
		}

		// Recursion
		if (typeof obj[newName] == 'object') {
			obj[newName] = camelCaseKeysToUnderscore(obj[newName]);
		}
	}

	return obj;
};

// This function coverts errors/faults returned from the OCAPI API to the format that is returned from the CAPI
// I added the fault key to make life easier as it's hard to discern a CAPI error
export const convertOcapiFaultToCapiError = (error) => {
	return {
		title: error.message,
		type: error.type,
		detail: error.message,
		// Unique to OCAPI I think
		arguments: error.arguments,
		fault: true
	};
};

// This function checks required parameters and or body for requests to OCAPI endpoints before sending
export const checkRequiredParameters = (listOfPassedParameters, listOfRequiredParameters) => {
	const isBodyOnlyRequiredParam =
		listOfRequiredParameters.includes('body') && listOfRequiredParameters.length === 1;

	if (!listOfPassedParameters.parameters && !isBodyOnlyRequiredParam) {
		return {
			title: `Parameters are required for this request`,
			type: `MissingParameters`,
			detail: `Parameters are required for this request`
		};
	}

	if (listOfRequiredParameters.includes('body') && !listOfPassedParameters.body) {
		return {
			title: `Body is required for this request`,
			type: `MissingBody`,
			detail: `Body is  required for this request`
		};
	}

	if (
		isBodyOnlyRequiredParam &&
		listOfRequiredParameters.includes('body') &&
		listOfPassedParameters.body
	) {
		return undefined;
	}

	let undefinedValues = listOfRequiredParameters.filter(
		(req) => !Object.keys(listOfPassedParameters.parameters).includes(req)
	);

	undefinedValues = undefinedValues.filter((value) => value !== 'body');

	if (undefinedValues.length) {
		return {
			title: `The following parameters were missing from your resquest: ${undefinedValues.toString()}`,
			type: `MissingParameters`,
			detail: `The following parameters were missing from your resquest: ${undefinedValues.toString()}`
		};
	} else {
		return undefined;
	}
};

// This function is used to interact with the OCAPI API
export const createOcapiFetch = (commerceAPIConfig) => async (
	endpoint,
	method,
	args,
	methodName,
	body
) => {
	const proxy = commerceAPIConfig.ocapiProxyPath;
	// The api config will only have `ocapiHost` during testing to workaround localhost proxy
	const host = commerceAPIConfig.ocapiHost
		? `https://${commerceAPIConfig.ocapiHost}`
		: `${getAppOrigin()}${proxy}`;
	const siteId = commerceAPIConfig.parameters.siteId;
	const headers = {
		...args[0].headers,
		'Content-Type': 'application/json',
		'x-dw-client-id': commerceAPIConfig.parameters.clientId
	};
	let response;
	response = await fetch(`${host}/s/${siteId}/dw/shop/v21_3/${endpoint}`, {
		method: method,
		headers: headers,
		...(body && {
			body: JSON.stringify(body)
		})
	});
	const httpStatus = response.status;

	if (!args[1] && response.json) {
		response = await response.json();
	}

	const convertedResponse = keysToCamel(response);

	if (convertedResponse.fault) {
		const error = convertOcapiFaultToCapiError(convertedResponse.fault);
		throw new HTTPError(httpStatus, error);
	} else {
		return convertedResponse;
	}
};

// This function derrives the SF Tenant Id from the SF OrgId
export const getTenantId = (orgId) => {
	// Derive tenant id id form org id
	const indexToStartOfTenantId = orgId.indexOf('_', orgId.indexOf('_') + 1);
	const tenantId = orgId.substring(indexToStartOfTenantId + 1);

	return tenantId;
};

/**
 * Indicates if an JSON response from the SDK should be considered an error
 * @param {object} jsonResponse - The response object returned from SDK calls
 * @returns {boolean}
 */
export const isError = (jsonResponse) => {
	if (!jsonResponse) {
		return false;
	}

	const {detail, title, type} = jsonResponse;

	if (detail && title && type) {
		return true;
	}

	return false;
};

/**
 * Decorator that wraps functions to handle error response.
 * @template {(...args: any[]) => Promise<any>} F
 * @param {F} func - A function which returns a promise
 * @returns {(...args: Parameters<F>) => ReturnType<F>}
 */
export const handleAsyncError = (func) => {
	return async (...args) => {
		const result = await func(...args);

		if (isError(result)) {
			throw new Error(result.detail);
		}

		return result;
	};
};

/**
 * Converts snake-case strings to space separated or sentence case
 * strings by replacing '_' with a ' '.
 * @param {string} text snake-case text.
 * @returns {string} space separated string.
 */
export const convertSnakeCaseToSentenceCase = (text) => {
	return text.split('_').join(' ');
};

/**
 * No operation function. You can use this
 * empty function when you wish to pass
 * around a function that will do nothing.
 * Usually used as default for event handlers.
 */
export const noop = () => {};
